import { TaskTypes } from "@/Types/Tasks"
import moment from "moment"
import { getExpectedEndDate } from "./ProgrammationHelper"

const getTaskById = (tasks: any[], id: string) => {

  return tasks.find((task: any) => task._id === id)
}

const getColorCodeById = (colorCodes: any[], id: string) => {

  return colorCodes.find((colorCode: any) => colorCode._id === id)
}

const getTaskType = (type: string, t: any) => {

  switch (type) {
      case 'partida': {
          return t('TASK_TYPES:Partida')
      }
      case 'trabajo': {
          return t('TASK_TYPES:Trabajo')
      }
      case 'tarea': {
          return t('TASK_TYPES:Task')
      }
      case 'diseño': {
          return t('TASK_TYPES:Design')
      }
      case 'reunion': {
          return t('TASK_TYPES:Meeting')
      }
      case 'hito': {
          return t('TASK_TYPES:Milestone')
      }
  }
}

const getBaseDays = (task: any, nonWorkingDays: string[]) => {

  const start = task.baseStart || task.startsAt
  const end = task.baseEnd || task.endsAt

  const day = moment(start)

  let days = 0

  while (day.isSameOrBefore(end, 'day')) {

    const isNonWorkingDay = nonWorkingDays.includes(day.format('YYYY-MM-DD'))

    if (!isNonWorkingDay) {

      days++
    }

    day.add(1, 'day')
  }

  return days
}

const getScheduledDays = (task: any, nonWorkingDays: string[]) => {

  const day = moment(task.startsAt)

  let days = 0

  while (day.isSameOrBefore(task.endsAt, 'day')) {

    const isNonWorkingDay = nonWorkingDays.includes(day.format('YYYY-MM-DD'))

    if (!isNonWorkingDay) {

      days++
    }

    day.add(1, 'day')
  }

  return days
}

const getActualDays = (task: any, nonWorkingDays: string[]) => {

  if (!task.startedAt || !task.endedAt) return ''

  const day = moment(task.startedAt)

  let days = 0

  while (day.isSameOrBefore(task.endedAt, 'day')) {

    const isNonWorkingDay = nonWorkingDays.includes(day.format('YYYY-MM-DD'))

    if (!isNonWorkingDay) {

      days++
    }

    day.add(1, 'day')
  }

  return days
}

const getFirstResponsible = (users: any[], responsibles: any[]) => {

  if (!responsibles.length) return ''

  const userId = responsibles[0]

  const user = users.find((user: any) => user._id === userId)

  return user ? `${user.name} ${user.surname}` : ''
}

const getStatus = (status: string, t: any) => {

  switch (status) {
      case 'activa': {
          return t('TASK_CARD:ActiveStatus')
      }
      case 'pendiente': {
          return t('TASK_CARD:PendingStatus')
      }
      case 'liberada': {
          return t('TASK_CARD:ReleasedStatus')
      }
      case 'completada': {
          return t('TASK_CARD:CompletedStatus')
      }
      case 'atrasada': {
          return t('TASK_CARD:OverdueStatus')
      }
      case 'activa-atrasada': {
          return t('TASK_CARD:ActiveOverdueStatus')
      }
  }
}

export const getExcelFirstSheetData = (tasks: any[], list: any[], users: any[], colorCodes: any[], nonWorkingDays: string[], t: any, parent?: any) => {

  const output: any[] = []

  list.forEach((item: any, i: number) => {

    const index = i + 1

    const position = parent ? `${parent.id}.${index}` : `${index}`

    if (item.type === 'group') {

      const group = {
        id: position,
        name: item.group.name,
      }

      const items = getExcelFirstSheetData(tasks, item.group.items, users, colorCodes, nonWorkingDays, t, group)

      output.push(...items)
    } else {

      const { task: taskId } = item

      const task = getTaskById(tasks, taskId)

      const colorCode = getColorCodeById(colorCodes, task.colorCode)

      const type = getTaskType(task.type, t)

      const actualDays = getActualDays(task, nonWorkingDays)

      const responsible = getFirstResponsible(users, task.responsibles)

      const status = getStatus(task.status, t)

      output.push({
        id: position,
        code: colorCode?.name || '',
        type,
        name: (task?.name || '').trim(),
        group: (parent?.name || '').trim(),
        baseStart: task.baseStart || task.startsAt,
        baseEnd: task.baseEnd || task.endsAt,
        baseDays: task.baseDays || task.executeDays || getBaseDays(task, nonWorkingDays),
        startsAt: task.startsAt,
        endsAt: task.endsAt,
        executeDays:task.executeDays || getScheduledDays(task, nonWorkingDays),
        startedAt: task.startedAt || '',
        endedAt: task.endedAt || '',
        actualDays,
        responsible,
        status,
        restrictions: task.restrictions.length,
        openRestrictions: task.restrictions.filter((r: any) => !r.checked).length,
        closedRestrictions: task.restrictions.filter((r: any) => r.checked).length,
      })
    }

    return output
  })

  return output
}

interface PeriodWeek {
  name: string
  week: string
  baseLineAcc: number
  scheduledAcc: number
  actualAcc: number
  projectAcc: number
  performance: number
  canAccumulateActual?: boolean
  isCurrentWeek?: boolean
  isAdditional?: boolean
}

const getWeeksInPeriod = (start: string, end: string, t: any): any => {

  const weeksObj: any = {}

  const startDate = moment(start)
  const endDate = moment(end)

  const week = startDate.clone()

  let sem = 1

  while (week.isSameOrBefore(endDate, 'week')) {

    const yearWeek = week.format('YYYYWW')

    const weekName = `${t('PROJECT_DASHBOARD:Week')} ${sem++}`
    const baseLineAcc = 0
    const baseLineMax = 0
    const scheduledAcc = 0
    const scheduledMax = 0
    const actualAcc = 0
    const actualMax = 0
    const projectAcc = 0
    const projectMax = 0
    const performance = 0
    const canAccumulateActual = week.isSameOrBefore(moment(), 'week')
    const isCurrentWeek = week.isSame(moment(), 'week')
    const isLastProjectWeek = week.isSame(moment(end, 'YYYY-MM-DD'), 'week')

    weeksObj[yearWeek] = {
      name: weekName,
      baseLineAcc,
      scheduledAcc,
      actualAcc,
      projectAcc,
      performance,
      canAccumulateActual,
      isCurrentWeek,
      isAdditional: false,
      isLastProjectWeek,
    }

    week.add(1, 'week')
  }

  return weeksObj
}

const dateToYMD = (date: Date): string => {

  return date.toISOString().slice(0, 10)
}


interface WeekAcc {
  baseLineAcc: number
  scheduledAcc: number
  actualAcc: number
}

const getISOWeek = (date: Date): string => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNum = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  const week = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
  const year = d.getUTCFullYear()

  return `${year}${String(week).padStart(2, '0')}`
}

const accumulateRange = (
  start: string | undefined,
  end: string | undefined,
  field: keyof WeekAcc,
  result: Record<string, WeekAcc>,
  weeksObj: Record<string, any>,
  nonWorkingSet: Set<string>
) => {
  if (!start || !end) return

  const current = new Date(start)
  const endDate = new Date(end)

  while (current <= endDate) {
    const dateStr = dateToYMD(current)

    if (!nonWorkingSet.has(dateStr)) {
      const weekKey = getISOWeek(current)

      if (weeksObj[weekKey]) {
        result[weekKey][field]++
      }
    }

    current.setDate(current.getDate() + 1)
  }
}

const accumulateActualRange = (
  start: string | undefined,
  hypotheticalEnd: string | undefined,
  progress: number,
  result: Record<string, WeekAcc>,
  weeksObj: Record<string, any>,
  nonWorkingSet: Set<string>
) => {
  if (!start || !hypotheticalEnd || progress <= 0) return

  const startDate = new Date(start)
  const endDate = new Date(hypotheticalEnd)

  // calcular duración total en días
  const totalDays =
    Math.floor((endDate.getTime() - startDate.getTime()) / 86400000) + 1

  // días completados según progreso
  const completedDays = Math.floor(totalDays * (progress / 100))

  if (completedDays <= 0) return

  const actualEnd = new Date(startDate)
  actualEnd.setDate(actualEnd.getDate() + completedDays - 1)

  const current = new Date(startDate)

  while (current <= actualEnd) {
    const dateStr = dateToYMD(current)

    if (!nonWorkingSet.has(dateStr)) {
      const weekKey = getISOWeek(current)

      if (weeksObj[weekKey]) {
        result[weekKey].actualAcc++
      }
    }

    current.setDate(current.getDate() + 1)
  }
}

const getTaskWeeksDaysAcc = (
  task: any,
  weeksObj: Record<string, any>,
  nonWorkingDays: string[]
) => {

  const result: Record<string, WeekAcc> = {}

  const nonWorkingSet = new Set(nonWorkingDays)

  // inicializar todas las semanas
  for (const week in weeksObj) {
    result[week] = {
      baseLineAcc: 0,
      scheduledAcc: 0,
      actualAcc: 0
    }
  }

  const baseStart = task.baseStart || task.startsAt
  const baseEnd = task.baseEnd || task.endsAt

  accumulateRange(baseStart, baseEnd, "baseLineAcc", result, weeksObj, nonWorkingSet)

  accumulateRange(task.startsAt, task.endsAt, "scheduledAcc", result, weeksObj, nonWorkingSet)

  const actualEnd = task.endedAt || getExpectedEndDate(task.startedAt, task.executeDays, nonWorkingDays, true)

  accumulateActualRange(task.startedAt, actualEnd, task.progress, result, weeksObj, nonWorkingSet)

  return result
}

export const getExcelSecondSheetData = (project: any, tasks: any[], nonWorkingDays: string[], t: any, parent?: any) => {

  const weeks = getWeeksInPeriod(project.startsAt, project.endsAt, t)

  tasks.forEach((task: any) => {

    const data = getTaskWeeksDaysAcc(task, weeks, nonWorkingDays)

    for (const key in data) {

      const week = data[key]

      weeks[key].baseLineAcc += week.baseLineAcc
      weeks[key].scheduledAcc += week.scheduledAcc
      weeks[key].actualAcc += week.actualAcc
    }
  })

  let baseLineAcc = 0
  let scheduledAcc = 0
  let actualAcc = 0

  let projectAcc = 0
  let secondTolastProjectAcc = 0

  let performance = 0

  let weekCounter = 0

  let lastWeekName = ''

  const progress: any = {
    baseline: 0,
    baselineMax: 0,
    scheduled: 0,
    scheduledMax: 0,
    actual: 0,
    actualMax: 0,
    project: 0,
    projectMax: 0,
  }

  let deviation = 0

  for(const week in weeks){

    lastWeekName = week

    weekCounter += 1

    const weekObj = weeks[week]

    const weekBaseLine = weekObj.baseLineAcc
    weekObj.baseLineAcc += baseLineAcc
    baseLineAcc += weekBaseLine

    progress.baselineMax = baseLineAcc

    const weekScheduled = weekObj.scheduledAcc
    weekObj.scheduledAcc += scheduledAcc
    scheduledAcc += weekScheduled

    progress.scheduledMax = scheduledAcc

    if (weekObj.canAccumulateActual) {

      const weekActual = weekObj.actualAcc
      weekObj.actualAcc += actualAcc
      actualAcc += weekActual

      progress.actualMax = actualAcc

      weekObj.performance = ''
    }

    if (weekObj.isCurrentWeek || (!performance && weekObj.isLastProjectWeek)) {

      projectAcc = weekObj.actualAcc
      performance = Number((projectAcc / weekObj.scheduledAcc).toFixed(2))

      weekObj.projectAcc = projectAcc
      progress.projectMax = Number((projectAcc).toFixed(0))
      weekObj.performance = performance

      progress.baseline = weekObj.baseLineAcc
      progress.scheduled = weekObj.scheduledAcc
      progress.actual = weekObj.actualAcc
      progress.project = weekObj.projectAcc

      deviation = progress.actual - progress.scheduled
    }

    if (!weekObj.canAccumulateActual) {

      secondTolastProjectAcc = projectAcc
      projectAcc = ((weekObj.scheduledAcc - projectAcc) * performance) + projectAcc

      progress.projectMax = Number((projectAcc).toFixed(0))

      weekObj.projectAcc = Number((projectAcc).toFixed(0))
      weekObj.actualAcc = ''
      weekObj.performance = ''
    }
  }

  let estimatedEndDate = ''

  if (projectAcc) {

    while (scheduledAcc > projectAcc) {

      const newWeekNumber = moment(lastWeekName, 'YYYYWW').add(1, 'week').format('YYYYWW')

      estimatedEndDate = moment(newWeekNumber, 'YYYYWW').endOf('week').format('DD/MM/YYYY')

      projectAcc = ((projectAcc - secondTolastProjectAcc) * performance) + projectAcc

      progress.projectMax = Number((projectAcc).toFixed(0))

      weeks[newWeekNumber] = {
        name: `${t('PROJECT_DASHBOARD:Week')} ${weekCounter + 1}`,
        baseLineAcc: '',
        scheduledAcc: '',
        actualAcc: '',
        projectAcc: Number((projectAcc).toFixed(0)),
        performance: '',
        canAccumulateActual: false,
        isCurrentWeek: false,
        isAdditional: true,
      }

      weekCounter += 1

      lastWeekName = newWeekNumber
    }
  }

  return {
    weeks,
    progress,
    estimatedEndDate,
    performance: performance * 100,
    deviation,
  }
}
